import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

const SECTIONS = [
  { key: 'founders', title: 'Founders' },
  { key: 'investors', title: 'Investors' },
  { key: 'options', title: 'Employees' }
];

const SHARE_CLASSES = ['Common', 'Preferred', 'Options'];

const VESTING_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: '4yr-1yr-cliff', label: '4 yr, 1 yr cliff' },
  { value: 'trigger-date', label: 'Trigger (date)' },
  { value: 'trigger-milestone', label: 'Trigger (milestone)' }
];

const isOptionRow = (row) => row.shareClass === 'Options';

const emptyRow = (shareClass = 'Common') => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  name: '',
  issueDate: '',
  terminationDate: '',
  shareClass,
  vestingSchedule: 'immediate',
  triggerDate: '',
  milestone: '',
  milestoneCompleted: false,
  milestoneCompletedDate: '',
  sharesIssued: '',
  exercisedOptions: '',
  sharesVested: ''
});

// Standard 4-year vesting with a 1-year cliff: 25% vests at the 1 year mark,
// the remaining 75% vests monthly over the following 3 years. A date trigger
// vests in full on/after its trigger date; a milestone trigger vests in full
// once the milestone is marked completed. Vesting stops at the termination date.
const computeVestedShares = (row) => {
  const issued = parseFloat(row.sharesIssued) || 0;
  if (!issued) return 0;

  const schedule = row.vestingSchedule;
  if (schedule === 'immediate') return issued;

  const today = new Date();
  const termination = row.terminationDate ? new Date(row.terminationDate + 'T00:00:00') : null;
  const asOf = termination && !Number.isNaN(termination.getTime()) && termination < today ? termination : today;

  if (schedule === 'trigger-milestone') {
    if (!row.milestoneCompleted) return 0;
    const done = row.milestoneCompletedDate ? new Date(row.milestoneCompletedDate + 'T00:00:00') : null;
    if (done && !Number.isNaN(done.getTime()) && done > asOf) return 0;
    return issued;
  }

  if (schedule === 'trigger-date' || schedule === 'trigger') {
    const trigger = (row.triggerDate || row.issueDate)
      ? new Date((row.triggerDate || row.issueDate) + 'T00:00:00')
      : null;
    if (!trigger || Number.isNaN(trigger.getTime())) return 0;
    return asOf >= trigger ? issued : 0;
  }

  // 4yr / 1yr cliff
  const issue = row.issueDate ? new Date(row.issueDate + 'T00:00:00') : null;
  if (!issue || Number.isNaN(issue.getTime())) return 0;
  if (asOf < issue) return 0;
  const monthsElapsed =
    (asOf.getFullYear() - issue.getFullYear()) * 12 + (asOf.getMonth() - issue.getMonth());
  if (monthsElapsed < 12) return 0;
  if (monthsElapsed >= 48) return issued;
  const fraction = 0.25 + ((monthsElapsed - 12) / 36) * 0.75;
  return Math.round(issued * fraction);
};

// Break a single row down into issued/reserved and vested/not-vested buckets.
// - Common/Preferred: the whole grant is "issued"; vesting splits it.
// - Options: the granted amount is "reserved" until exercised (paid for), at
//   which point the exercised portion becomes issued shares.
const rowBreakdown = (row) => {
  const granted = parseFloat(row.sharesIssued) || 0;
  const vested = computeVestedShares(row);

  if (!isOptionRow(row)) {
    return {
      fullyDiluted: granted,
      issued: granted,
      issuedVested: vested,
      issuedNotVested: Math.max(0, granted - vested),
      reserved: 0,
      reservedVested: 0,
      reservedNotVested: 0
    };
  }

  const exercised = Math.min(parseFloat(row.exercisedOptions) || 0, granted);
  const reserved = Math.max(0, granted - exercised);
  const reservedVested = Math.max(0, Math.min(vested, granted) - exercised);
  return {
    fullyDiluted: granted,
    issued: exercised,
    issuedVested: exercised,
    issuedNotVested: 0,
    reserved,
    reservedVested,
    reservedNotVested: Math.max(0, reserved - reservedVested)
  };
};

const formatNumber = (n) => (Number.isFinite(n) ? n.toLocaleString() : '0');
const formatPct = (n) => (Number.isFinite(n) ? `${n.toFixed(2)}%` : '0.00%');
const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const CapTable = ({ userInfo }) => {
  const [data, setData] = useState({ founders: [], investors: [], options: [], totalSharesFullyDiluted: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);

  // Access control: admins and founders/super clients can edit; everyone else
  // with access views a read-only table.
  const accountType = userInfo?.accountType;
  const role = userInfo?.role;
  const canEdit =
    accountType === 'admin' ||
    accountType === 'superadmin' ||
    accountType === 'super_user' ||
    accountType === 'super_client' ||
    accountType === 'founder' ||
    role === 'super_user' ||
    role === 'super_client' ||
    role === 'founder';

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/cap-table`);
        if (resp.ok) {
          const d = await resp.json();
          setData({
            founders: d.founders || [],
            investors: d.investors || [],
            options: d.options || [],
            totalSharesFullyDiluted: d.totalSharesFullyDiluted || ''
          });
        } else {
          setError('Failed to load cap table');
        }
      } catch (e) {
        console.error('Error loading cap table:', e);
        setError('Failed to load cap table');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allRows = [...data.founders, ...data.investors, ...data.options];
  const breakdowns = allRows.map(rowBreakdown);

  const sum = (fn) => breakdowns.reduce((acc, b) => acc + fn(b), 0);
  const totalIssued = sum(b => b.issued);
  const issuedVested = sum(b => b.issuedVested);
  const issuedNotVested = sum(b => b.issuedNotVested);
  const totalReserved = sum(b => b.reserved);
  const reservedVested = sum(b => b.reservedVested);
  const reservedNotVested = sum(b => b.reservedNotVested);

  // Total Shares (Fully Diluted) is the editable denominator for fully-diluted
  // ownership. If left blank, fall back to the sum of issued + reserved so the
  // percentages still add up.
  const enteredTotalShares = parseFloat(data.totalSharesFullyDiluted) || 0;
  const fullyDilutedDenominator = enteredTotalShares || (totalIssued + totalReserved);
  const availableForIssuance = Math.max(0, enteredTotalShares - totalIssued - totalReserved);

  const updateRow = (sectionKey, index, field, value) => {
    setData(prev => {
      const rows = [...prev[sectionKey]];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, [sectionKey]: rows };
    });
  };

  const addRow = (sectionKey) => {
    setData(prev => ({
      ...prev,
      [sectionKey]: [...prev[sectionKey], emptyRow(sectionKey === 'options' ? 'Options' : 'Common')]
    }));
  };

  const removeRow = (sectionKey, index) => {
    setData(prev => ({ ...prev, [sectionKey]: prev[sectionKey].filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const withVested = (rows) => rows.map(r => ({ ...r, sharesVested: computeVestedShares(r) }));
      const payload = {
        founders: withVested(data.founders),
        investors: withVested(data.investors),
        options: withVested(data.options),
        totalSharesFullyDiluted: data.totalSharesFullyDiluted
      };
      const resp = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/cap-table`, payload);
      if (resp.ok) {
        const d = await resp.json();
        setData({
          founders: d.founders || [],
          investors: d.investors || [],
          options: d.options || [],
          totalSharesFullyDiluted: d.totalSharesFullyDiluted || ''
        });
        alert('Cap table saved successfully!');
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to save cap table');
      }
    } catch (e) {
      console.error('Error saving cap table:', e);
      alert('Failed to save cap table');
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const resp = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/cap-table/history`);
      if (resp.ok) {
        const d = await resp.json();
        setHistory(d.history || []);
      } else {
        setHistory([]);
      }
    } catch (e) {
      console.error('Error loading cap table history:', e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history.length === 0) loadHistory();
  };

  // Load a saved version's data back into the editable table. This does not
  // persist until the user clicks Save Changes (which creates a new version).
  const loadVersionIntoEditor = (ct) => {
    if (!window.confirm('Load this version into the editor? Any unsaved changes will be replaced. Nothing is saved until you click Save Changes.')) return;
    setData({
      founders: ct.founders || [],
      investors: ct.investors || [],
      options: ct.options || [],
      totalSharesFullyDiluted: ct.totalSharesFullyDiluted || ''
    });
    setShowHistory(false);
    setExpandedVersion(null);
  };

  const inputCls =
    'w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-600';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cap table...</p>
        </div>
      </div>
    );
  }

  const SummaryStat = ({ label, value }) => (
    <div>
      <p className="text-sm font-bold text-gray-500">{label}</p>
      <p className="text-lg text-gray-900">{value}</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cap Table</h1>
          <p className="text-gray-600 mt-2">
            Capitalization table by Founders, Investors, and Employees
            {!canEdit && <span className="ml-2 text-amber-600 font-medium">(View only)</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={toggleHistory}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {showHistory ? 'Hide History' : 'View History'}
          </button>
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Version history */}
      {showHistory && (
        <div className="card p-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Saved Versions</h2>
          {historyLoading ? (
            <p className="text-gray-500 text-sm">Loading history...</p>
          ) : history.length === 0 ? (
            <p className="text-gray-500 text-sm">No saved versions yet.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">Click a version to view its full details.</p>
              {history.map((v, i) => {
                const ct = v.capTable || {};
                const lineItems = (ct.founders || []).length + (ct.investors || []).length + (ct.options || []).length;
                const isOpen = expandedVersion === i;
                return (
                  <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedVersion(isOpen ? null : i)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm">{isOpen ? '▼' : '▶'}</span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{formatDateTime(v.savedAt)}</p>
                          <p className="text-xs text-gray-500">
                            Saved by {v.savedBy || 'unknown'} · {lineItems} line item{lineItems === 1 ? '' : 's'} ·
                            {' '}Total Shares (Fully Diluted): {formatNumber(parseFloat(ct.totalSharesFullyDiluted) || 0)}
                          </p>
                        </div>
                      </div>
                      {canEdit && (
                        <button
                          onClick={(e) => { e.stopPropagation(); loadVersionIntoEditor(ct); }}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          title="Load this version into the editor (does not save until you click Save Changes)"
                        >
                          Load into editor
                        </button>
                      )}
                    </div>
                    {isOpen && (
                      <div className="border-t border-gray-200 p-3 bg-gray-50 space-y-4">
                        {SECTIONS.map(section => {
                          const rows = ct[section.key] || [];
                          const isEmployees = section.key === 'options';
                          return (
                            <div key={section.key}>
                              <h4 className="text-sm font-semibold text-gray-700 mb-1">{section.title}</h4>
                              {rows.length === 0 ? (
                                <p className="text-xs text-gray-400">No entries.</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 text-xs bg-white">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        {['Name', 'Share Class', 'Vesting', 'Trigger',
                                          isEmployees ? 'Options Issued' : 'Shares Issued',
                                          isEmployees ? 'Options Vested' : 'Shares Vested',
                                          ...(isEmployees ? ['Exercised'] : []),
                                          'Issue Date', 'Termination Date'].map((h, hi) => (
                                          <th key={hi} className="px-2 py-1 text-left font-medium text-gray-500 whitespace-nowrap">{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {rows.map((r, ri) => {
                                        const trigger = r.vestingSchedule === 'trigger-milestone'
                                          ? `${r.milestone || '—'}${r.milestoneCompleted ? ' (completed' + (r.milestoneCompletedDate ? ' ' + r.milestoneCompletedDate : '') + ')' : ''}`
                                          : (r.triggerDate || (r.vestingSchedule && r.vestingSchedule.startsWith('trigger') ? r.issueDate : '') || '—');
                                        return (
                                          <tr key={ri}>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.name || '—'}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.shareClass || '—'}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.vestingSchedule || '—'}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{trigger}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{formatNumber(parseFloat(r.sharesIssued) || 0)}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{formatNumber(parseFloat(r.sharesVested) || 0)}</td>
                                            {isEmployees && <td className="px-2 py-1 whitespace-nowrap">{formatNumber(parseFloat(r.exercisedOptions) || 0)}</td>}
                                            <td className="px-2 py-1 whitespace-nowrap">{r.issueDate || '—'}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.terminationDate || '—'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="card p-4 mb-6">
        <div className="mb-4">
          <label className="text-sm font-bold text-gray-500 block mb-1">Total Shares (Fully Diluted)</label>
          <p className="text-xs text-gray-400 mb-1">
            Shares available for issuance and the denominator used for fully diluted ownership.
          </p>
          <input
            type="number"
            value={data.totalSharesFullyDiluted}
            onChange={(e) => setData(prev => ({ ...prev, totalSharesFullyDiluted: e.target.value }))}
            disabled={!canEdit}
            placeholder="e.g. 10000000"
            className={`${inputCls} max-w-xs text-lg`}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 border-t pt-4">
          <SummaryStat label="Shares Available for Issuance" value={formatNumber(availableForIssuance)} />
          <SummaryStat label="Shares Issued (vested)" value={formatNumber(issuedVested)} />
          <SummaryStat label="Shares Issued (not vested)" value={formatNumber(issuedNotVested)} />
          <SummaryStat label="Shares Reserved (vested)" value={formatNumber(reservedVested)} />
          <SummaryStat label="Shares Reserved (not vested)" value={formatNumber(reservedNotVested)} />
          <SummaryStat label="Total Issued / Reserved" value={`${formatNumber(totalIssued)} / ${formatNumber(totalReserved)}`} />
        </div>
      </div>

      {SECTIONS.map(section => {
        const isEmployees = section.key === 'options';
        const issuedHeader = isEmployees ? 'Options Issued' : 'Shares Issued';
        const vestedHeader = isEmployees ? 'Options Vested' : 'Shares Vested';
        const headers = ['Name', 'Issue Date', 'Termination Date', 'Share Class', 'Vesting Schedule',
          'Trigger', issuedHeader, vestedHeader];
        if (isEmployees) headers.push('Exercised Options');
        headers.push('% Ownership', 'Fully Diluted % Ownership');
        if (canEdit) headers.push('');

        return (
          <div key={section.key} className="card p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
              {canEdit && (
                <button
                  onClick={() => addRow(section.key)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  + Add Line
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data[section.key].length === 0 ? (
                    <tr>
                      <td colSpan={headers.length} className="px-3 py-4 text-gray-500 text-center">
                        No entries yet.{canEdit ? ` Click "+ Add Line" to add a line.` : ''}
                      </td>
                    </tr>
                  ) : (
                    data[section.key].map((row, index) => {
                      const bd = rowBreakdown(row);
                      const vested = computeVestedShares(row);
                      const ownership = totalIssued ? (bd.issued / totalIssued) * 100 : 0;
                      const fullyDiluted = fullyDilutedDenominator ? (bd.fullyDiluted / fullyDilutedDenominator) * 100 : 0;
                      const isTriggerDate = row.vestingSchedule === 'trigger-date' || row.vestingSchedule === 'trigger';
                      const isTriggerMilestone = row.vestingSchedule === 'trigger-milestone';
                      return (
                        <tr key={row.id || index}>
                          <td className="px-3 py-2 min-w-[200px]">
                            <input
                              type="text"
                              value={row.name}
                              onChange={(e) => updateRow(section.key, index, 'name', e.target.value)}
                              className={`${inputCls} min-w-[180px]`}
                              placeholder="Name"
                              disabled={!canEdit}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={row.issueDate}
                              onChange={(e) => updateRow(section.key, index, 'issueDate', e.target.value)}
                              className={inputCls}
                              disabled={!canEdit}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="date"
                              value={row.terminationDate}
                              onChange={(e) => updateRow(section.key, index, 'terminationDate', e.target.value)}
                              className={inputCls}
                              disabled={!canEdit}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={row.shareClass}
                              onChange={(e) => updateRow(section.key, index, 'shareClass', e.target.value)}
                              className={inputCls}
                              disabled={!canEdit}
                            >
                              {SHARE_CLASSES.map(sc => <option key={sc} value={sc}>{sc}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={row.vestingSchedule === 'trigger' ? 'trigger-date' : row.vestingSchedule}
                              onChange={(e) => updateRow(section.key, index, 'vestingSchedule', e.target.value)}
                              className={inputCls}
                              disabled={!canEdit}
                            >
                              {VESTING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2 min-w-[180px]">
                            {isTriggerDate ? (
                              <input
                                type="date"
                                value={row.triggerDate}
                                onChange={(e) => updateRow(section.key, index, 'triggerDate', e.target.value)}
                                className={inputCls}
                                disabled={!canEdit}
                                title="Date on which shares/options are issued"
                              />
                            ) : isTriggerMilestone ? (
                              <div className="space-y-1">
                                <input
                                  type="text"
                                  value={row.milestone}
                                  onChange={(e) => updateRow(section.key, index, 'milestone', e.target.value)}
                                  className={inputCls}
                                  placeholder="Milestone"
                                  disabled={!canEdit}
                                />
                                <label className="flex items-center gap-1 text-xs text-gray-600">
                                  <input
                                    type="checkbox"
                                    checked={!!row.milestoneCompleted}
                                    onChange={(e) => updateRow(section.key, index, 'milestoneCompleted', e.target.checked)}
                                    disabled={!canEdit}
                                  />
                                  Completed
                                </label>
                                {row.milestoneCompleted && (
                                  <input
                                    type="date"
                                    value={row.milestoneCompletedDate}
                                    onChange={(e) => updateRow(section.key, index, 'milestoneCompletedDate', e.target.value)}
                                    className={inputCls}
                                    disabled={!canEdit}
                                    title="Date the milestone was completed"
                                  />
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={row.sharesIssued}
                              onChange={(e) => updateRow(section.key, index, 'sharesIssued', e.target.value)}
                              className={inputCls}
                              placeholder="0"
                              disabled={!canEdit}
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">{formatNumber(vested)}</td>
                          {isEmployees && (
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                value={row.exercisedOptions}
                                onChange={(e) => updateRow(section.key, index, 'exercisedOptions', e.target.value)}
                                className={inputCls}
                                placeholder="0"
                                disabled={!canEdit}
                                title="Options that have been exercised (paid for) and become issued shares"
                              />
                            </td>
                          )}
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">{formatPct(ownership)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-900">{formatPct(fullyDiluted)}</td>
                          {canEdit && (
                            <td className="px-3 py-2">
                              <button
                                onClick={() => removeRow(section.key, index)}
                                className="text-red-600 hover:text-red-700"
                                title="Remove line"
                              >
                                ✕
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {isEmployees && (
              <p className="text-xs text-gray-500 mt-3">
                Options are counted as <strong>reserved</strong> until exercised (paid for), at which point the
                exercised amount becomes issued shares. You can add multiple lines for the same person (e.g. grants
                with different issue dates or milestones).
              </p>
            )}
          </div>
        );
      })}

      <p className="text-xs text-gray-500 mb-6">
        Vested amounts, % Ownership, and Fully Diluted % Ownership are calculated automatically.
        % Ownership is based on issued (outstanding) shares; Fully Diluted % Ownership uses Total Shares (Fully Diluted)
        as the denominator. A 4 yr / 1 yr cliff schedule vests 25% after one year, then the remaining 75% monthly over
        the next three years. A date trigger vests in full on/after the trigger date; a milestone trigger vests once the
        milestone is marked completed.
      </p>
    </div>
  );
};

export default CapTable;
